import { trace as createPerformanceTrace, type FirebasePerformance, type PerformanceTrace } from 'firebase/performance';

import { perf } from '@/firebase-config';
import { logger } from '@/utils/logger';

import type { TelemetryClient, TelemetryTraceOptions } from '../contracts/telemetry-client';

const MAX_CUSTOM_ATTRIBUTES = 5;
const MAX_CUSTOM_ATTRIBUTE_NAME_LENGTH = 32;
const MAX_CUSTOM_NAME_LENGTH = 100;

type SanitizedTraceOptions = {
  attributes?: Record<string, string>;
  metrics?: Record<string, number>;
};

function isValidTraceOrMetricName(name: string): boolean {
  return name.length > 0 &&
    name.length <= MAX_CUSTOM_NAME_LENGTH &&
    name.trim() === name &&
    !name.startsWith('_');
}

function isValidAttributeName(name: string): boolean {
  return name.length > 0 &&
    name.length <= MAX_CUSTOM_ATTRIBUTE_NAME_LENGTH &&
    /^[A-Za-z][A-Za-z_]*$/u.test(name);
}

function toAttributeValue(value: unknown): string | null {
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, MAX_CUSTOM_NAME_LENGTH);
}

function sanitizeAttributes(attributesInput?: Record<string, unknown>): Record<string, string> | undefined {
  const attributes: Record<string, string> = {};
  for (const [name, value] of Object.entries(attributesInput ?? {})) {
    if (Object.keys(attributes).length >= MAX_CUSTOM_ATTRIBUTES) break;
    if (!isValidAttributeName(name)) continue;

    const attributeValue = toAttributeValue(value);
    if (attributeValue === null) continue;

    attributes[name] = attributeValue;
  }

  return Object.keys(attributes).length > 0 ? attributes : undefined;
}

function sanitizeMetrics(metricsInput?: Record<string, number>): Record<string, number> | undefined {
  const metrics: Record<string, number> = {};
  for (const [name, value] of Object.entries(metricsInput ?? {})) {
    if (!isValidTraceOrMetricName(name) || !Number.isFinite(value)) continue;
    metrics[name] = value;
  }

  return Object.keys(metrics).length > 0 ? metrics : undefined;
}

function sanitizeTraceOptions(options?: TelemetryTraceOptions): SanitizedTraceOptions | undefined {
  if (!options) return;

  const attributes = sanitizeAttributes(options.attributes);
  const metrics = sanitizeMetrics(options.metrics);
  const sanitized: SanitizedTraceOptions = {};
  if (attributes) {
    sanitized.attributes = attributes;
  }
  if (metrics) {
    sanitized.metrics = metrics;
  }

  return sanitized.attributes || sanitized.metrics ? sanitized : undefined;
}

function applyTraceOptions(activeTrace: PerformanceTrace, options?: SanitizedTraceOptions): void {
  for (const [name, value] of Object.entries(options?.attributes ?? {})) {
    activeTrace.putAttribute(name, value);
  }
  for (const [name, value] of Object.entries(options?.metrics ?? {})) {
    activeTrace.putMetric(name, value);
  }
}

function canRecordTrace(startTime: number, duration: number): boolean {
  return Number.isFinite(startTime) &&
    Number.isFinite(duration) &&
    startTime >= 0 &&
    duration >= 0;
}

export class FirebaseTelemetryClient implements TelemetryClient {
  private readonly getPerformanceClient: () => FirebasePerformance | null;

  constructor(
    getPerformanceClient: () => FirebasePerformance | null = () => perf,
  ) {
    this.getPerformanceClient = getPerformanceClient;
  }

  async trace<T>(
    name: string,
    operation: () => Promise<T>,
    options?: TelemetryTraceOptions,
  ): Promise<T> {
    const activeTrace = this.createTrace(name, options);
    let didStartTrace = false;

    if (activeTrace) {
      try {
        activeTrace.start();
        didStartTrace = true;
      } catch (error: unknown) {
        logger.warn('[FirebaseTelemetryClient] Failed to start Performance trace:', error);
      }
    }

    try {
      return await operation();
    } finally {
      if (activeTrace && didStartTrace) {
        try {
          activeTrace.stop();
        } catch (error: unknown) {
          logger.warn('[FirebaseTelemetryClient] Failed to stop Performance trace:', error);
        }
      }
    }
  }

  recordTrace(
    name: string,
    startTime: number,
    duration: number,
    options?: TelemetryTraceOptions,
  ): void {
    if (!canRecordTrace(startTime, duration)) return;

    const activeTrace = this.createTrace(name, options);
    if (!activeTrace) return;

    try {
      activeTrace.record(startTime, duration, sanitizeTraceOptions(options));
    } catch (error: unknown) {
      logger.warn('[FirebaseTelemetryClient] Failed to record Performance trace:', error);
    }
  }

  private createTrace(name: string, options?: TelemetryTraceOptions): PerformanceTrace | null {
    const performance = this.getPerformanceClient();
    if (!performance) return null;

    if (!isValidTraceOrMetricName(name)) {
      logger.warn(`[FirebaseTelemetryClient] Ignored invalid Performance trace name: ${name}`);
      return null;
    }

    try {
      const activeTrace = createPerformanceTrace(performance, name);
      applyTraceOptions(activeTrace, sanitizeTraceOptions(options));
      return activeTrace;
    } catch (error: unknown) {
      logger.warn('[FirebaseTelemetryClient] Failed to create Performance trace:', error);
      return null;
    }
  }
}
